// Theme + density tokens. Single source of truth for the CSS custom-prop
// values consumed by apps/web's `<ThemeProvider>` / `<DensityProvider>`.
// Held as plain TypeScript constants so the Settings → Preferences tab,
// the storage layer, and the providers all agree on the shape.

export type ThemeMode = 'light' | 'dark' | 'system';
export type Density = 'compact' | 'comfortable';
export type CurrencyDisplay = 'eur-primary' | 'original-primary';

export const THEME_MODES: readonly ThemeMode[] = ['light', 'dark', 'system'];
export const DENSITIES: readonly Density[] = ['compact', 'comfortable'];
export const CURRENCY_DISPLAYS: readonly CurrencyDisplay[] = ['eur-primary', 'original-primary'];

// ui-spec §9.4 — 36 px compact (default) / 44 px comfortable. Row heights
// are derived from this constant in apps/web so changing density updates
// every cell uniformly via the `--row-height` custom property.
export const DENSITY_ROW_HEIGHT_PX: Record<Density, number> = {
  compact: 36,
  comfortable: 44,
};

// ui-spec §7.12 / §9.3 — Linear-inspired clean white as the default canvas;
// dark mode inverts to a near-neutral dark blue, not pure black. Both
// palettes ship from day 1.
export interface ThemePalette {
  readonly bg: string;
  readonly fg: string;
  readonly muted: string;
  readonly border: string;
  readonly soft: string;
  readonly accent: string;
}

export const THEME_PALETTES: Record<'light' | 'dark', ThemePalette> = {
  light: {
    bg: '#ffffff',
    fg: '#08090a',
    muted: '#6e7178',
    border: '#e6e8eb',
    soft: '#f4f5f8',
    accent: '#5e6ad2',
  },
  dark: {
    bg: '#0c0f17',
    fg: '#e6e8eb',
    muted: '#8a8f98',
    border: '#1f2330',
    soft: '#161a24',
    accent: '#7a85ee',
  },
};

export function paletteForMode(mode: ThemeMode, systemPrefersDark: boolean): ThemePalette {
  if (mode === 'system') return systemPrefersDark ? THEME_PALETTES.dark : THEME_PALETTES.light;
  return THEME_PALETTES[mode];
}
